import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import "../../RessourcesStyles/HorsCatalogue.css";
import "../../RessourcesStyles/DemandeFormModal.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import { clearDemandeMessage, createDemandeManagerBulkRequest } from "../../../../../redux/actions/demandesActions";

import { Users } from "lucide-react";

const initial = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
};

export default function HorsCatalogueManager({
    campagneId,
    hideCampagneSelect = false,
    onCancel = () => {},
    onSuccess,
}) {
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const campagnesState = useSelector((s) => s.campagnes || {});
    const refState = useSelector((s) => s.paramDomainesThemes || {});
    const usersState = useSelector((s) => s.user || {});
    const demandesState = useSelector((s) => s.demandes || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = refState.domaines || refState.domainesList || [];
    const themes = refState.themes || refState.themesList || [];
    const teamUsers = usersState.directReports || [];

    const [form, setForm] = useState(initial);
    const [localCampagneId, setLocalCampagneId] = useState("");
    const [openPick, setOpenPick] = useState(false);
    const [openFields, setOpenFields] = useState(false);
    const [pickedUsers, setPickedUsers] = useState([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const campagneIdEff = hideCampagneSelect ? campagneId : localCampagneId;
    const loading = !!demandesState.loading;

    useEffect(() => {
        if (uid) dispatch(getDirectReportsRequest(uid));
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
        dispatch(clearDemandeMessage());
    }, [dispatch, uid]);

    useEffect(() => {
        if (!hideCampagneSelect && !localCampagneId && campagnes?.length) {
            setLocalCampagneId(String(campagnes[0].id));
        }
    }, [hideCampagneSelect, campagnes, localCampagneId]);

    useEffect(() => {
        if (campagneIdEff) {
            setForm((state) => ({ ...state, campagneId: String(campagneIdEff) }));
        }
    }, [campagneIdEff]);

    useEffect(() => {
        if (!submitAttempted || loading) return;

        if (demandesState.error) {
            setSubmitAttempted(false);
            return;
        }

        if (demandesState.message) {
            Swal.fire({
                icon: "success",
                text: demandesState.message || "Votre demande a été envoyée avec succès.",
            });
            setForm((state) => ({
                ...initial,
                campagneId: hideCampagneSelect && campagneId ? String(campagneId) : state.campagneId,
            }));
            setPickedUsers([]);
            setOpenPick(false);
            setOpenFields(false);
            setSubmitAttempted(false);
            dispatch(clearDemandeMessage());
            if (onSuccess) onSuccess();
        }
    }, [campagneId, demandesState.error, demandesState.message, dispatch, hideCampagneSelect, loading, onSuccess, submitAttempted]);

    const themesFiltered = useMemo(() => {
        const domaineId = form.domaineId ? Number(form.domaineId) : null;
        if (!domaineId) return themes || [];
        return (themes || []).filter((theme) => Number(theme?.domaineId || theme?.domaine?.id) === domaineId);
    }, [themes, form.domaineId]);

    const update = (key, value) => setForm((state) => ({ ...state, [key]: value }));

    const openSelector = () => {
        if (!campagneIdEff) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionnez d'abord une campagne.",
                target: document.body,
            });
            return;
        }
        if (!form.action?.trim()) {
            Swal.fire({
                icon: "warning",
                text: "L'intitulé de la formation est obligatoire.",
                target: document.body,
            });
            return;
        }
        setOpenPick(true);
    };

    const onConfirmUsers = (ids) => {
        if (!ids?.length) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionnez au moins un collaborateur.",
                target: document.body,
            });
            return;
        }
        const uniqueIds = Array.from(new Set(ids || []));
        const users = (teamUsers || []).filter((user) => uniqueIds.includes(user.id));
        setPickedUsers(users);
        setOpenPick(false);
        setOpenFields(true);
    };

    const onSubmitFields = (rows) => {
        const payload = {
            campagneId: Number(campagneIdEff),
            type: "HORS_CATALOGUE",
            domaineId: form.domaineId ? Number(form.domaineId) : null,
            themeId: form.themeId ? Number(form.themeId) : null,
            action: form.action.trim(),
            description: form.description?.trim() || null,
            items: (rows || []).map((row) => ({
                targetUserId: Number(row.targetUserId),
                mode: row.mode || null,
                period: row.period || null,
                niveau: row.niveau || null,
                certifiante: !!row.certifiante,
                priorite: row.priorite != null ? Number(row.priorite) : null,
                objectifs: row.objectifs || null,
                objectifs2: row.objectifs2 || null,
                objectifs3: row.objectifs3 || null,
            })),
        };

        dispatch(clearDemandeMessage());
        setSubmitAttempted(true);
        dispatch(createDemandeManagerBulkRequest(payload));
    };

    return (
        <>
            <div className="demande-form">
                <div className="field">
                    <label>Intitulé de la formation *</label>
                    <input
                        value={form.action}
                        onChange={(event) => update("action", event.target.value)}
                        placeholder="Saisissez l'intitulé de la formation"
                    />
                </div>

                <div className="demande-grid-2">
                    {!hideCampagneSelect && (
                        <div className="field">
                            <label>Campagne</label>
                            <select value={campagneIdEff || ""} onChange={(event) => setLocalCampagneId(event.target.value)}>
                                <option value="">-</option>
                                {(campagnes || []).map((campagne) => (
                                    <option key={campagne.id} value={campagne.id}>
                                        {campagne.nom || campagne.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="field">
                        <label>Domaine</label>
                        <select
                            value={form.domaineId}
                            onChange={(event) => {
                                update("domaineId", event.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">-</option>
                            {(domaines || []).map((domaine) => (
                                <option key={domaine.id} value={domaine.id}>
                                    {domaine.nom || domaine.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="field">
                        <label>Thème</label>
                        <select value={form.themeId} onChange={(event) => update("themeId", event.target.value)}>
                            <option value="">-</option>
                            {(themesFiltered || []).map((theme) => (
                                <option key={theme.id} value={theme.id}>
                                    {theme.nom || theme.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>Commentaire</label>
                    <textarea
                        value={form.description}
                        onChange={(event) => update("description", event.target.value)}
                        rows={5}
                    />
                </div>

                <div className="demande-actions">
                    <button className="btn btn-light" type="button" onClick={onCancel} disabled={loading}>
                        Annuler
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={openSelector}
                        disabled={!campagneIdEff || loading}
                    >
                        <Users size={16} />
                        Choisir les collaborateurs
                    </button>
                </div>

                {!campagneIdEff && <div className="demande-hint">Sélectionnez d'abord une campagne.</div>}
                {demandesState.error && <div className="demande-hint">Une erreur est survenue lors de l'envoi de la demande.</div>}
            </div>

            <UsersPickerModal
                open={openPick}
                onClose={() => setOpenPick(false)}
                users={teamUsers}
                title="Sélectionner des collaborateurs"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openFields}
                onClose={() => setOpenFields(false)}
                users={pickedUsers}
                title="Paramétrage par collaborateur"
                onSubmit={onSubmitFields}
            />
        </>
    );
}
